'use client';

import { useProductLink } from '@dealerportal/catalog-ui/Hooks/useProductLink';
import { useVariantLoader } from '@dealerportal/catalog-ui/Hooks/useVariantLoader';
import type { PaginatedProduct, Swatches } from '@dealerportal/common';
import { formatNumber, useTranslations } from '@dealerportal/i18n/client';
import {
  useClientContext,
  useFavouriteProducts,
} from '@dealerportal/react-helpers';
import { BetterButton } from '@dealerportal/ui-components/BetterButton';
import { remapBrandName, remapProductName } from '@dealerportal/utilities';
import classNames from 'classnames';
import uniqBy from 'es-toolkit/compat/uniqBy';
import React, { type FC, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { SAMPLE_ICON_SVG, VISUALIZER_ICON_SVG } from './icons';
import { MarkFavourite } from './MarkFavourite';
import { MissingImage } from './MissingImage';
import { ProductCardSwatches } from './ProductCardSwatches';
import { ProductLink } from './ProductLink';
import { ProductQuickViewDialog } from './ProductQuickViewDialog';
import { ProductTags } from './ProductTags';
import { Samples } from './Samples';
import { SPEC_TOOLTIP_KEYS, SpecPill } from './SpecPill';

export interface Product {
  swatchImage: string;
}

interface Props {
  product: PaginatedProduct;
  showSwatches?: boolean;
  disableOrderSample?: boolean;
  hidePricing?: boolean;
  imageClassNames?: string;
}

export const ProductCard: FC<Props> = ({
  product,
  showSwatches = false,
  disableOrderSample: disableOrderSampleProp,
  hidePricing = false,
  imageClassNames,
}: Props) => {
  const t = useTranslations('products');
  const tSpecs = useTranslations('specs');
  const [currentProduct, setCurrentProduct] = React.useState(product);
  const [isOpen, setIsOpen] = React.useState(false);
  const [showButtonLoader, setShowButtonLoader] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [isTouched, setIsTouched] = React.useState(false);
  const isQuickViewVisible = isHovered || isFocused || isTouched;
  const [isQuickViewOpen, setIsQuickViewOpen] = React.useState(false);
  const [activeTooltip, setActiveTooltip] = React.useState<string | null>(null);
  const [tooltipRect, setTooltipRect] = React.useState<DOMRect | null>(null);
  const cardRef = React.useRef<HTMLDivElement | null>(null);

  const handlePillHover = (label: string | null, rect?: DOMRect) => {
    setActiveTooltip(label);
    setTooltipRect(rect ?? null);
  };
  const favouriteContext = useFavouriteProducts();
  const ctx = useClientContext();
  const disableOrderSample = disableOrderSampleProp ?? ctx.disableOrderSample;
  const { generateProductLink } = useProductLink();
  const {
    ref: variantRef,
    swatches: loadedSwatches,
    colorCount,
  } = useVariantLoader(product);

  useEffect(() => {
    setCurrentProduct(product);
  }, [product]);

  useEffect(() => {
    if (!isTouched) return;
    const dismiss = (e: TouchEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        setIsTouched(false);
      }
    };
    document.addEventListener('touchstart', dismiss);
    return () => document.removeEventListener('touchstart', dismiss);
  }, [isTouched]);

  const handleFavouriteClick = useCallback(() => {
    if (favouriteContext?.addRemoveProductsFromFav) {
      const isFavourite = favouriteContext.find({ UPID: currentProduct.UPID });
      favouriteContext.addRemoveProductsFromFav(currentProduct);

      if (!isFavourite) {
        toast(t('addedToFavorites'));
      }
    }
  }, [currentProduct, favouriteContext]);

  const swatchLimit = 7;
  const uniqueProductSwatches = uniqBy(loadedSwatches, 'UPID')?.sort((a, b) =>
    a.colorName.localeCompare(b.colorName)
  );
  const [swatches, setSwatches] = React.useState(
    uniqueProductSwatches.slice(0, swatchLimit)
  );

  useEffect(() => {
    const updated = uniqBy(loadedSwatches, 'UPID')?.sort((a, b) =>
      a.colorName.localeCompare(b.colorName)
    );
    setSwatches(updated.slice(0, swatchLimit));
  }, [loadedSwatches]);

  const handleSwatchClick = useCallback(() => {
    swatches.length < uniqueProductSwatches.length
      ? setSwatches(uniqueProductSwatches)
      : setSwatches(uniqueProductSwatches.slice(0, swatchLimit));
  }, [swatches, uniqueProductSwatches]);

  let tags = currentProduct?.tags ?? [];
  tags = tags.filter((t) => t !== 'Order_Sample');
  const sampleSwatches: Swatches[] = uniqueProductSwatches.filter(
    (x) => x.hasSample
  );

  // The current product may not be in uniqueProductSwatches (e.g. spread colors, or products
  // whose swatches haven't loaded yet), so add it explicitly if it has a sample and is absent,
  // then re-sort to keep the list in colorName order for ProductQuickViewDialog.
  if (
    currentProduct.hasSample &&
    !sampleSwatches.some((s) => s.UPID === currentProduct.UPID)
  ) {
    sampleSwatches.push({ ...currentProduct });
    sampleSwatches.sort((a, b) => a.colorName.localeCompare(b.colorName));
  }

  const vizToken =
    ctx.visualizerSettings !== undefined
      ? ctx.visualizerSettings.showVisualizer
        ? (ctx.visualizerSettings.apiKey ?? '')
        : ''
      : (ctx.siteTokens.visualizerApiToken ?? '');

  let tooltipStyle: React.CSSProperties | null = null;
  let caretLeft = '50%';
  if (
    activeTooltip &&
    SPEC_TOOLTIP_KEYS[activeTooltip] &&
    tooltipRect &&
    cardRef.current
  ) {
    const cardRect = cardRef.current.getBoundingClientRect();
    const tooltipWidth = 224; // w-56
    const pillCenterX =
      tooltipRect.left - cardRect.left + tooltipRect.width / 2;
    const rawLeft = pillCenterX - tooltipWidth / 2;
    const clampedLeft = Math.max(
      8,
      Math.min(rawLeft, cardRect.width - tooltipWidth - 8)
    );
    tooltipStyle = {
      top: tooltipRect.top - cardRect.top - 8,
      left: clampedLeft,
      transform: 'translateY(-100%)',
    };
    caretLeft = `${pillCenterX - clampedLeft - 5}px`;
  }

  return (
    <div
      ref={(el) => {
        variantRef.current = el;
        cardRef.current = el;
      }}
      key={`${currentProduct.id}`}
      className="product-card ui-justify-between ui-rounded ui-relative ui-isolate ui-bg-white"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onTouchStart={() => setIsTouched(true)}
    >
      <div className="ui-h-full ui-flex ui-flex-col">
        <div className="ui-relative ui-overflow-hidden ui-rounded-t">
          <div className="!ui-absolute ui-p-2 ui-w-full ui-box-border">
            <div className="ui-relative ui-flex ui-gap-2 ui-w-full ui-items-start ui-justify-between">
              <div className="ui-flex ui-gap-2">
                {tags?.length ? (
                  <ProductTags
                    isOpen={isOpen}
                    onTagClick={() => setIsOpen((prev) => !prev)}
                    tags={tags}
                  />
                ) : null}
              </div>
              {favouriteContext && (
                <MarkFavourite
                  btnClass={classNames(
                    '!ui-min-w-8 !ui-min-h-8 !ui-w-8 !ui-h-8 ui-shrink-0',
                    isOpen ? '!ui-hidden' : 'ui-block'
                  )}
                  iconClass="!ui-w-5 !ui-h-5"
                  isFavourite={favouriteContext?.find({
                    UPID: currentProduct.UPID,
                  })}
                  onClick={handleFavouriteClick}
                />
              )}
            </div>
          </div>

          <div
            className={classNames(
              'ui-absolute ui-inset-0 ui-z-[101] ui-flex ui-items-center ui-justify-center ui-transition-opacity ui-duration-200 ui-pointer-events-none',
              isQuickViewVisible ? 'ui-opacity-100' : 'ui-opacity-0'
            )}
          >
            <BetterButton
              className="ui-pointer-events-auto"
              btnStyle="neutral"
              label={t('quickView').toUpperCase()}
              icon='<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"/></svg>'
              iconSize="4"
              replaceClass={{
                'ui-rounded-md': 'ui-rounded-full',
              }}
              attributes={{
                type: 'button',
                onClick: (e) => {
                  e.preventDefault();
                  setIsTouched(false);
                  setIsQuickViewOpen(true);
                },
              }}
            />
          </div>

          <ProductLink
            product={currentProduct}
            className="ui-h-[100%] ui-block ui-z-[100]"
            id={`product-link-${currentProduct.UPID}`}
          >
            {!currentProduct.imageURL && (
              <MissingImage
                className={classNames(
                  'product-card__image ui-fill-gray-400/50 ui-bg-gray-100',
                  imageClassNames
                )}
                height={290}
                width={290}
              />
            )}
            {currentProduct.imageURL && (
              <img
                className={classNames(
                  'product-card__image ui-object-cover',
                  imageClassNames
                )}
                alt={`${currentProduct.productName} by ${currentProduct.brandName}`}
                title={`${currentProduct.productName} by ${currentProduct.brandName}`}
                style={{ width: '100%' }}
                src={
                  currentProduct.imageURL.includes('res.cloudinary.com')
                    ? currentProduct.imageURL.replace(
                        'https://images.floorforce.com/Textures/',
                        ''
                      )
                    : currentProduct.imageURL
                }
                height={290}
                width={290}
              />
            )}
          </ProductLink>
        </div>

        {(currentProduct.acRating ||
          currentProduct.wearLayer ||
          currentProduct.material ||
          (currentProduct.thickness ?? 0) > 0) && (
          <div className="ui-flex ui-flex-wrap ui-gap-1 ui-px-3 ui-pt-2">
            {currentProduct.acRating && (
              <SpecPill
                specId="acRating"
                value={`${tSpecs('acRatingPrefix')} ${currentProduct.acRating}`}
                onHover={handlePillHover}
              />
            )}
            {currentProduct.wearLayer && (
              <SpecPill
                specId="wearLayerThickness"
                value={`${currentProduct.wearLayer} ${tSpecs('wearLayerSuffix')}`}
                onHover={handlePillHover}
              />
            )}
            {currentProduct.material && (
              <SpecPill
                specId="material"
                value={
                  Array.isArray(currentProduct.material)
                    ? currentProduct.material.join(', ')
                    : currentProduct.material
                }
                onHover={handlePillHover}
              />
            )}
            {(currentProduct.thickness ?? 0) > 0 && (
              <SpecPill
                specId="thickness"
                value={`${formatNumber(currentProduct.thickness ?? 0)}${tSpecs('thicknessSuffix')}`}
                onHover={handlePillHover}
              />
            )}
          </div>
        )}

        <div className="ui-flex-1 ui-flex ui-flex-col">
          <div
            className={classNames('ui-p-4 ui-pb-6 ui-space-y-2', {
              '!ui-pb-4': currentProduct.hasVisualization,
            })}
          >
            <div className="ui-flex ui-items-center ui-text-sm ui-font-semibold ui-text-gray-700 ui-gap-1 ui-flex-wrap">
              <span className="product-card__brand">
                {remapBrandName(currentProduct.brandName)}
              </span>
              {!showSwatches && colorCount > 1 && (
                <ProductLink
                  product={currentProduct}
                  className="product-card__colors-link !ui-flex !ui-items-center ui-gap-1.5 !ui-text-xs ui-ml-auto ui-whitespace-nowrap !ui-font-bold !ui-text-gray-400 ui-font-[inherit]"
                >
                  <span>{t('seeColors', { count: colorCount })}</span>
                  <svg
                    className="ui-w-2 ui-mt-px ui-shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320 512"
                  >
                    <path
                      className="ui-fill-gray-400"
                      d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"
                    />
                  </svg>
                </ProductLink>
              )}
            </div>

            <div>
              <ProductLink
                product={currentProduct}
                className="!ui-font-semibold !ui-text-gray-900"
              >
                <div className="product-card__line">
                  {remapProductName(currentProduct.productName)}
                </div>

                <div className="product-card__color ui-font-normal">
                  {currentProduct.colorName}
                </div>

                {currentProduct.additionalMessage && (
                  <div className="product-card__color ui-font-normal ui-mt-1 ui-text-sm product-card__additional-message">
                    {currentProduct.additionalMessage}
                  </div>
                )}
              </ProductLink>

              {showSwatches && swatches?.length > 1 && (
                <div className="product-card__swatch_thumbnails ui-flex ui-gap-2 ui-mt-4">
                  <ProductCardSwatches
                    swatches={swatches}
                    selectedSwatch={currentProduct}
                    itemClassName="ui-h-[26px] ui-w-[26px] ui-rounded-md"
                    onMouseEnter={(swatch) => {
                      setCurrentProduct({
                        ...currentProduct,
                        ...swatch,
                      });

                      if (
                        currentProduct.hasSample &&
                        swatch.UPID !== currentProduct.UPID
                      ) {
                        setShowButtonLoader(true);

                        setTimeout(() => {
                          setShowButtonLoader(false);
                        }, 250);
                      }
                    }}
                    onSelect={(swatch) => {
                      // On mobile, override onclick to switch the current color in the card rather than go to the color's PDP page
                      if (!window.matchMedia('(min-width: 768px)').matches) {
                        setCurrentProduct({
                          ...currentProduct,
                          ...swatch,
                        });
                      }
                    }}
                  />
                  {uniqueProductSwatches.length > swatchLimit && (
                    <BetterButton
                      btnStyle="simple"
                      label={
                        swatches.length !== uniqueProductSwatches.length
                          ? `+${uniqueProductSwatches.length - swatchLimit}`
                          : `<svg class="ui-w-3 ui-fill-gray-700" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M432 256c0 17.7-14.3 32-32 32L48 288c-17.7 0-32-14.3-32-32s14.3-32 32-32l352 0c17.7 0 32 14.3 32 32z"/></svg>`
                      }
                      className="ui-shrink-0 ui-h-6 ui-w-6 ui-items-center ui-justify-center ui-rounded-full"
                      labelClassName="ui-flex ui-items-center ui-justify-center ui-text-xs ui-text-gray-700 ui-font-semibold"
                      attributes={{
                        onClick: handleSwatchClick,
                      }}
                    />
                  )}
                </div>
              )}

              {!hidePricing &&
                currentProduct.unitPrice > 0 &&
                currentProduct.priceDescription && (
                  <div className="product-card__price ui-mt-4">
                    {currentProduct.unitPrice && (
                      <div className="ui-flex ui-items-center ui-gap-1.5">
                        <div className="ui-text-xl ui-font-bold ui-leading-none ui-text-gray-800">
                          {`$${currentProduct.unitPrice.toFixed(2)}`}
                        </div>
                        /
                        <div className="ui-relative ui-text-xs ui-text-gray-800 ui-font-bold ui-leading-none">
                          {currentProduct.priceDescription}
                        </div>
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>

          {((!disableOrderSample && sampleSwatches.length > 0) ||
            currentProduct.hasVisualization) && (
            <div className="ui-flex ui-flex-col ui-gap-1 ui-pb-4 ui-pt-1 ui-px-4 ui-mt-auto">
              {/* One block: each hidden Samples keeps an empty wrapper, which
                  would otherwise add a flex gap per swatch and shift the button.
                  Hidden (not unmounted) when the current color has no sample, so
                  the Samples stay mounted but the empty block adds no gap. */}
              {!disableOrderSample && sampleSwatches.length > 0 && (
                <div
                  className={classNames({
                    'ui-hidden': !sampleSwatches.some(
                      (s) => s.UPID === currentProduct.UPID
                    ),
                  })}
                >
                  {sampleSwatches.map((swatch) => (
                    <Samples
                      key={swatch.UPID}
                      icon={SAMPLE_ICON_SVG}
                      samplePrice={0}
                      productInfo={swatch}
                      visible={currentProduct.UPID === swatch.UPID}
                      isLoading={showButtonLoader}
                      replaceClass={{
                        'ui-h-12': 'ui-h-10',
                        '!ui-px-6': 'ui-px-3',
                        '!ui-w-max': '!ui-w-full',
                      }}
                    />
                  ))}
                </div>
              )}
              {currentProduct.hasVisualization && vizToken && (
                <div className="product-card__viz-button">
                  <BetterButton
                    btnStyle={currentProduct.hasSample ? 'neutral' : 'primary'}
                    href={`${generateProductLink(currentProduct)}#vis`}
                    label={t('seeInYourRoom')}
                    icon={VISUALIZER_ICON_SVG}
                    replaceClass={{
                      'ui-h-12': 'ui-h-10',
                      '!ui-px-6': currentProduct.hasSample
                        ? 'ui-px-2.5'
                        : 'ui-px-3',
                      '!ui-w-max': '!ui-w-full',
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {isQuickViewOpen && (
        <ProductQuickViewDialog
          product={currentProduct}
          colorSwatches={uniqueProductSwatches}
          sampleSwatches={disableOrderSample ? [] : sampleSwatches}
          onClose={() => {
            setIsQuickViewOpen(false);
            setIsFocused(false);
            setIsTouched(false);
          }}
        />
      )}

      {tooltipStyle && activeTooltip && SPEC_TOOLTIP_KEYS[activeTooltip] && (
        <div
          role="tooltip"
          className="ui-pointer-events-none ui-absolute ui-z-[300] ui-bg-gray-900
          ui-rounded-xl ui-shadow-2xl ui-px-3.5 ui-py-3 ui-w-56 ui-leading-none"
          style={tooltipStyle}
        >
          <span className="ui-text-xs ui-text-gray-300 ui-block ui-mb-1 ui-uppercase">
            {tSpecs(activeTooltip as Parameters<typeof tSpecs>[0])}
          </span>
          <span className="ui-text-xs ui-text-white">
            {tSpecs(
              SPEC_TOOLTIP_KEYS[activeTooltip] as Parameters<typeof tSpecs>[0]
            )}
          </span>
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: caretLeft,
              borderWidth: '5px',
              borderStyle: 'solid',
              borderColor: 'rgb(17 24 39) transparent transparent transparent',
            }}
          />
        </div>
      )}
    </div>
  );
};
